import React, {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {setSchedulingData} from '../../../redux/actions/courseManagementActions';
import {useHistory} from 'react-router-dom';
import {formatDateInUserTimezone} from '../../../utils/date';
import RadioButtonsNewComponent from '../radioButtonsNew';
import {PUBLISH_TYPE} from '../../courseManagement/CourseWizard/Constant';
import styles from './CourseSchdulingTab.module.scss';
import {IS_JAPAN, IS_THAILAND} from '../../../utils/datePicker';
import {useDateValidation} from '../../hooks/useDateValidation';

const CourseSchedulingTab = ({
  selectedPublishType,
  setSelectedPublishType = () => {},
  showHeading = true,
  isDisabled = false,
  setIsSchedulingFormValid = () => {},
  courseData = {}
}) => {
  const latestLiveScheduledTimestamp = useSelector(
    (state) => state?.courseManagement?.latestLiveScheduledTimestamp || null
  );

  const [maxScheduleError, setMaxScheduleError] = useState(null);
  const { DATE_FORMAT, validateDate, validateTimeWithCondition, validateStartTime } = useDateValidation(
    latestLiveScheduledTimestamp?.scheduledTimestamp,
    setMaxScheduleError
  );

  const dispatch = useDispatch();
  const router = useHistory();

  useEffect(() => {
    if (courseData?.scheduledTimestamp) {
      setSelectedPublishType(PUBLISH_TYPE.SCHEDULED_PUBLISHING)
    }
  }, [])

  const { startDate: initialStartDate, startTime: initialStartTime } = useMemo(() => {
    const timestamp = courseData?.scheduledTimestamp;
    if (!timestamp) return { startDate: '', startTime: '' };
  
    const { formattedDate } = formatDateInUserTimezone(timestamp, DATE_FORMAT, 'HH:mm');
    const [startDate, startTime] = formattedDate.split(' ');
    return { startDate, startTime };
  }, [courseData?.scheduledTimestamp]);
  

  const {
    register,
    watch,
    trigger,
    setValue,  // Used to set values for the inputs if needed
    resetField, // Add resetField from useForm
    formState: { errors, touchedFields },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      startDate: initialStartDate,
      startTime: initialStartTime,
    },
  });

  const startDate = watch('startDate');
  const startTime = watch('startTime');

  useEffect(() => {
    const validate = async () => {
      if (selectedPublishType === PUBLISH_TYPE.PUBLISH_NOW) {
        setIsSchedulingFormValid(true);
        return;
      }

      // Manually trigger validation and wait for result
      const result = await trigger(['startDate', 'startTime']);

      const hasErrors =
        !result ||          // form-level invalid
        Boolean(errors?.startDate) ||
        Boolean(errors?.startTime) ||
        (selectedPublishType === PUBLISH_TYPE.SCHEDULED_PUBLISHING && (startDate === '' || startTime === '')) ||
        Boolean(maxScheduleError);

      setIsSchedulingFormValid(!hasErrors);
    };

    validate();
  }, [selectedPublishType, startDate, startTime, maxScheduleError]);

  useEffect(() => {
    console.log('startDate: ', {startDate,startTime})
    dispatch(
      setSchedulingData({
        startDate,
        startTime,
      })
    );
  }, [startDate, startTime]);

  return (
    <div>
      {showHeading && <h3 className={styles.publishing_heading}>Publishing</h3>}
      <h4 className={styles.visibility_heading} style={{fontSize: showHeading ? '24px' : '18px' }}>Visibility{showHeading ? '*' : ''}</h4>

      <p className={styles.description}>Choose when to make this course visible.</p>

      <div>
        <RadioButtonsNewComponent
          id={PUBLISH_TYPE.PUBLISH_NOW}
          fontSize={16}
          checked={selectedPublishType === PUBLISH_TYPE.PUBLISH_NOW}
          label={PUBLISH_TYPE.PUBLISH_NOW}
          onChangeHandler={() => {
            if (!isDisabled) {
              setSelectedPublishType(PUBLISH_TYPE.PUBLISH_NOW);
              resetField("startDate", { defaultValue: "", keepError: false }); // clear the publish date field when switching radio
            }
          }}
        />

        <RadioButtonsNewComponent
          id={PUBLISH_TYPE.SCHEDULED_PUBLISHING}
          fontSize={16}
          checked={selectedPublishType === PUBLISH_TYPE.SCHEDULED_PUBLISHING}
          label={PUBLISH_TYPE.SCHEDULED_PUBLISHING}
           onChangeHandler={() => {
            if (!isDisabled) {
              setSelectedPublishType(PUBLISH_TYPE.SCHEDULED_PUBLISHING);
              resetField("startTime", { defaultValue: "", keepError: false }); // clear the publish time when switching radio
            }
          }}
        />
      </div>

      {selectedPublishType === PUBLISH_TYPE.SCHEDULED_PUBLISHING && (
        <div className="mt-3">
          <div className={[styles.form_field, styles.form_field_row].join(' ')}>
            {/* Start Date Field */}
            <div
              className={[styles.left_field, errors.startDate ? styles.error_wrapper : ''].join(
                ' '
              )}
            >
              <div className={styles.label_wrapper}>
                <div className={styles.label_text}>Publish Date*</div>
              </div>
              <div className={styles.input_wrapper}>
                <input
                  type={'text'}
                  placeholder={IS_THAILAND ? 'DD/MM/YYYY' : DATE_FORMAT}
                  {...register('startDate', {
                    validate: (date) => {
                      if (!date) return true;
                      return validateDate(date.trim(), 'Start'); 
                    },
                  })}
                  className={styles.input}
                  maxLength={IS_JAPAN ? 11: 10}
                />
                {touchedFields.startDate && errors.startDate ? <div className={styles.error_icon_red}></div> : null}
              </div>
              {touchedFields.startDate && errors.startDate && (
                <div className={styles.error_message}>{errors.startDate.message}</div>
              )}
            </div>

            {/* Start Time Field */}
            <div
              className={[styles.right_field, errors.startTime ? styles.error_wrapper : ''].join(' ')}
            >
              <div className={styles.label_wrapper}>
                <div className={styles.label_text}>Publish Time*</div>
              </div>
              <div className={styles.input_wrapper}>
                <input
                  type={'text'}
                  placeholder={'00:00'}
                  {...register('startTime', {
                    validate: (time) => {
                      if (!time) return true;
                      return validateTimeWithCondition(time?.trim(), () =>
                        validateStartTime(time?.trim(), startDate?.trim())
                      );
                    },
                  })}
                  className={styles.input}
                  maxLength={5}
                />
                {touchedFields.startTime && errors.startTime ? <div className={styles.error_icon_red}></div> : null}
              </div>
              {touchedFields.startTime && errors.startTime && (
                <div className={styles.error_message}>{errors.startTime.message}</div>
              )}
            </div>
          </div>

          <p className={styles.description}>A course may be scheduled up to 30 days in advance.</p>
        </div>
      )}

      {selectedPublishType === PUBLISH_TYPE.SCHEDULED_PUBLISHING && maxScheduleError && (
        <div className={[styles.review_publish_lp].join(' ')}>
          <div className={styles.card_wrapper}>
            <div
              className={[
                styles.card_gutters,
                styles.error_review_publish,
                `col-md-12 col-lg-12`,
              ].join(' ')}
            >
              <div className={styles.card}>
                <div className={styles.status_icon} />
                <div className={styles.details_wrapper}>
                  <div className={styles.title}>Publish date needs revision</div>
                  <div className={styles.sub_title}>
                    This course cannot be scheduled because its publish date is inconsistent with
                    the publish date of the Learning Path. The maximum allowed publish date is:{' '}
                    <b>{maxScheduleError}</b>.
                  </div>
                </div>
                <div
                  className={styles.go_page}
                  onClick={() => {
                    if (latestLiveScheduledTimestamp?.learningPathId) {
                      router.push({
                        pathname: '/learning-path-wizard',
                        query: { id: null },
                        search: `?id=${latestLiveScheduledTimestamp?.learningPathId}&sub_header=review_and_publish`,
                      });
                    }
                  }}
                >
                  Go To Page
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseSchedulingTab;
